import React, { useState } from 'react';
import { AlertTriangle, Loader2, X, Send } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { useToast } from '@/components/ui/use-toast';

const TIME_SLOTS = [
  '09:00', '10:00', '11:00', '12:00', '13:00',
  '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00',
];

function tomorrowISO() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

export default function SystemUpdateReschedulePanel({ booking, onClose }) {
  const { toast } = useToast();
  const [suggestedDate, setSuggestedDate] = useState(tomorrowISO());
  const [suggestedTime, setSuggestedTime] = useState(booking.time_slot || '12:00');
  const [driverName, setDriverName] = useState(booking.customer_name || '');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await base44.functions.invoke('sendSystemUpdateRescheduleNotice', {
        bookingId: booking.id,
        suggestedDate,
        suggestedTime,
        driverName: driverName.trim() || undefined,
      });
      const data = res.data || res;
      const emailOk = data?.email?.sent;
      const smsOk = data?.sms?.sent;

      if (emailOk && smsOk) {
        toast({ title: 'Reschedule notice sent', description: 'Email + SMS delivered to the customer.' });
        onClose();
      } else if (emailOk || smsOk) {
        const sent = [];
        if (emailOk) sent.push('email');
        if (smsOk) sent.push('SMS');
        const failed = [];
        if (!emailOk) failed.push(data?.email?.error || 'email failed');
        if (!smsOk) failed.push(data?.sms?.error || 'SMS failed');
        toast({
          title: `Partially sent (${sent.join(' + ')})`,
          description: failed.join(' · '),
          variant: 'destructive',
        });
        onClose();
      } else {
        const msg = [data?.email?.error, data?.sms?.error].filter(Boolean).join(' · ') || 'Send failed';
        throw new Error(msg);
      }
    } catch (e) {
      toast({ title: 'Could not send notice', description: e?.message || 'Please try again.', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border border-yellow-400/40 bg-[#161616] p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-yellow-400" />
          <p className="text-yellow-400 text-[10px] font-bold tracking-widest">SYSTEM UPDATE RESCHEDULE</p>
        </div>
        <button onClick={onClose} className="text-white/30 hover:text-white">
          <X className="w-4 h-4" />
        </button>
      </div>

      <p className="text-white/40 text-xs leading-relaxed">
        Sends a branded reschedule email and SMS explaining the booking system was briefly live during testing. Offers a new slot and a complimentary drive voucher. Replies are routed to WhatsApp 07411254009.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-white/30 text-[10px] font-bold tracking-widest mb-1">SUGGESTED DATE</label>
          <input
            type="date"
            value={suggestedDate}
            onChange={(e) => setSuggestedDate(e.target.value)}
            className="w-full bg-[#0d0d0f] border border-white/10 text-white text-sm px-3 py-2 focus:outline-none focus:border-yellow-400/60"
          />
        </div>
        <div>
          <label className="block text-white/30 text-[10px] font-bold tracking-widest mb-1">SUGGESTED TIME</label>
          <select
            value={suggestedTime}
            onChange={(e) => setSuggestedTime(e.target.value)}
            className="w-full bg-[#0d0d0f] border border-white/10 text-white text-sm px-3 py-2 focus:outline-none focus:border-yellow-400/60"
          >
            {TIME_SLOTS.map((t) => (
              <option key={t} value={t} className="bg-[#0d0d0f]">{t}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-white/30 text-[10px] font-bold tracking-widest mb-1">DRIVER NAME</label>
          <input
            value={driverName}
            onChange={(e) => setDriverName(e.target.value)}
            placeholder="Defaults to customer name"
            className="w-full bg-[#0d0d0f] border border-white/10 text-white text-sm px-3 py-2 focus:outline-none focus:border-yellow-400/60"
          />
        </div>
      </div>

      <div className="flex flex-wrap justify-end gap-2 pt-1">
        <button
          onClick={onClose}
          className="text-white/50 hover:text-white text-[10px] font-bold tracking-widest px-4 py-2 transition-colors"
        >
          CANCEL
        </button>
        <button
          onClick={handleSend}
          disabled={sending || !suggestedDate || !suggestedTime}
          className="bg-yellow-400 hover:bg-yellow-400/90 text-black font-bold text-[10px] tracking-widest px-5 py-2.5 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          {sending ? 'SENDING…' : 'SEND EMAIL + SMS'}
        </button>
      </div>
    </div>
  );
}